import { useState, useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
} from 'recharts';
import { getPriceHistory } from '../lib/api';
import { cn } from '../lib/utils';
import type { PriceHistoryResponse } from '../lib/types';

interface MiniPriceChartProps {
  cardName: string;
  setName: string;
  cardNumber?: string;
}

type ChartDataPoint = {
  price: number;
  timestamp: number;
};

export function MiniPriceChart({
  cardName,
  setName,
  cardNumber,
}: MiniPriceChartProps) {
  const [data, setData] = useState<PriceHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadPriceHistory() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getPriceHistory(cardName, setName, cardNumber, '30d');

        if (response.success) {
          setData(response);
        } else {
          setError(response.error || 'Failed to load');
        }
      } catch (err) {
        console.error('Price history error:', err);
        setError('Unable to load');
      } finally {
        setIsLoading(false);
      }
    }

    loadPriceHistory();
  }, [cardName, setName, cardNumber]);

  // Transform price history data for the chart
  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (!data?.price_history) return [];

    return data.price_history.map((point) => ({
      timestamp: point.t,
      price: point.p,
    }));
  }, [data]);

  // Get change values for 30d
  const change = data?.price_change_30d ?? null;
  const isPositive = change !== null && change > 0;
  const isNegative = change !== null && change < 0;


  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-16 items-center justify-center">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-outline border-t-primary" />
      </div>
    );
  }

  // Error or no data state
  if (error || chartData.length === 0) {
    return (
      <div className="flex h-16 items-center justify-center">
        <Minus className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      {/* Mini chart */}
      <div className="h-12">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
            <defs>
              <linearGradient id={`miniGradient-${cardName}`} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={isPositive ? '#22c55e' : isNegative ? '#ef4444' : '#9ca3af'}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={isPositive ? '#22c55e' : isNegative ? '#ef4444' : '#9ca3af'}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="price"
              stroke={isPositive ? '#22c55e' : isNegative ? '#ef4444' : '#9ca3af'}
              strokeWidth={1.5}
              fill={`url(#miniGradient-${cardName})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Trend indicator */}
      <div className="mt-1 flex items-center justify-center gap-1">
        {isPositive && <TrendingUp className="h-3 w-3 text-green-500" />}
        {isNegative && <TrendingDown className="h-3 w-3 text-red-400" />}
        {!isPositive && !isNegative && <Minus className="h-3 w-3 text-muted-foreground" />}
        <span
          className={cn(
            'text-xs font-medium',
            isPositive && 'text-green-500',
            isNegative && 'text-red-400',
            !isPositive && !isNegative && 'text-muted-foreground'
          )}
        >
          {change !== null ? (
            <>
              {change > 0 ? '+' : ''}
              {change.toFixed(1)}%
            </>
          ) : (
            '--'
          )}
        </span>
      </div>
    </div>
  );
}
